import { axiosApiInstance } from 'Store';
import {
    CalculateByParamRequest,
    CalculateByParamResponse,
    InvestmentObject,
    SavedInvestmentFilter
} from 'utils/types/AnalyticPanelModule';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

const getDefaultFilter = async (): Promise<InvestmentObject | null> => {
    return axiosApiInstance
        .get('/investment/filter/default')
        .then((resp) => resp.data as InvestmentObject)
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getDefaultFilter (analyticPanel)' });

            return null;
        });
};

const getSavedFilters = async (): Promise<SavedInvestmentFilter[]> => {
    return axiosApiInstance
        .get('/investment/filter')
        .then((resp) => {
            if (resp.status === 204 || !resp.data) {
                return [];
            }

            const filters = Array.isArray(resp.data) ? resp.data : resp.data.filters || [];

            return filters as SavedInvestmentFilter[];
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getSavedFilters (analyticPanel)' });

            return [];
        });
};

const getFilter = async (filterId: number): Promise<InvestmentObject | null> => {
    return axiosApiInstance
        .get(`/investment/filter/${filterId}`)
        .then((resp) => resp.data as InvestmentObject)
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getFilter (analyticPanel)' });
            activeNotification('Problem z załadowaniem', 'Nie udało się załadować filtra inwestycji', 'danger');

            return null;
        });
};

const deleteFilter = async (filterId: number): Promise<void> => {
    return axiosApiInstance
        .delete(`/investment/filter/${filterId}`)
        .then(() => {
            activeNotification('Udało się!', 'Filtr inwestycji został usunięty', 'success');
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'deleteFilter (analyticPanel)' });
            activeNotification('Problem z usunięciem', 'Nie udało się usunąć filtra inwestycji', 'danger');
        });
};

const createFilter = async (
    params: InvestmentObject
): Promise<InvestmentObject | null> => {
    return axiosApiInstance
        .post('/investment/filter', params)
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr inwestycji został zapisany', 'success');

            return resp.data as InvestmentObject;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'createFilter (analyticPanel)' });
            activeNotification('Problem z zapisem', 'Nie udało się zapisać filtra inwestycji', 'danger');

            return null;
        });
};

const updateFilter = async (
    params: InvestmentObject
): Promise<InvestmentObject | null> => {
    return axiosApiInstance
        .put('/investment/filter', params)
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr inwestycji został zaktualizowany', 'success');

            return resp.data as InvestmentObject;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'updateFilter (analyticPanel)' });
            activeNotification('Problem z aktualizacją', 'Nie udało się zaktualizować filtra inwestycji', 'danger');

            return null;
        });
};

const calculateInvestment = async (
    params: InvestmentObject
): Promise<InvestmentObject | null> => {
    return axiosApiInstance
        .post('/investment/filter/predict', params)
        .then((resp) => {
            return resp.data as InvestmentObject;
        })
        .catch((err) => {
            // @todo: zmienić obługę błędu po modyfikacji API
            const showNotificaition = err?.response?.status === 400 && err?.response?.error === 'Value cannot be null or negative, field=flipProfitAmount';
            const notShowNotification = err?.response?.status === 400 && err?.response?.error !== 'Value cannot be null or negative, field=flipProfitPercent';

            if (showNotificaition) {
                activeNotification('Nie można obliczyć zysku', 'Nie można obliczyć zysku, ponieważ wartość jest ujemna lub równa zero. Proszę sprawdzić wprowadzone dane.', 'warning');
            }

            if (!showNotificaition || !notShowNotification) {
                const errorId = v4();
                userApiRequests.logException(err, errorId, { functionName: 'calculateInvestment (analyticPanel)' });
                activeNotification('Błąd obliczeń', 'Nie udało się obliczyć analizy inwestycji', 'warning');
            }

            return null;
        });
};

const calculateByParam = async (
    params: CalculateByParamRequest
): Promise<CalculateByParamResponse | null> => {
    return axiosApiInstance
        .post('/investment/filter/calculate', params)
        .then((resp) => {
            const data = resp.data;

            if (typeof data === 'number' || typeof data === 'string') {
                return { value: Number(data) } as CalculateByParamResponse;
            }

            return data as CalculateByParamResponse;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'calculateByParam (analyticPanel)' });
            activeNotification('Błąd przeliczenia', 'Nie udało się przeliczyć wartości parametru', 'warning');

            return null;
        });
};

export const analyticPanelApiRequests = {
    getDefaultFilter,
    getSavedFilters,
    getFilter,
    deleteFilter,
    createFilter,
    updateFilter,
    calculateInvestment,
    calculateByParam
};
